import { useEffect } from 'react';
import { debounce } from 'lodash-es';
import type { useMap } from 'react-map-gl';
import { areasConfig } from '../datasets/areas-utils';
import { subsurfaceMapsConfig } from '../datasets/subsurface-maps.config';
import type { TileLayerConfig } from '../types';
import {
  docLayerId,
  haloLayerId,
  outlineLayerId,
} from '../map-section/tile-layer-utils';
import { CELLS_LAYER_ID } from './utils';

type MapboxMap = ReturnType<
  NonNullable<ReturnType<typeof useMap>['current']>['getMap']
>;

/** A style layer, as much of it as the ordering reads. */
export interface StyleLayer {
  id: string;
  source?: unknown;
}

/** A style source; the ordering reads only a vector or raster source's url. */
export type StyleSource = object;

/**
 * A dataset's tile layer ids in draw order: each fill, then the outline it draws from
 * the same source.
 * @param {TileLayerConfig[]} layers The dataset's tile layers.
 * @return {string[]} The layer ids, bottom first.
 */
const tileLayerIds = (layers: TileLayerConfig[] = []): string[] =>
  layers.flatMap((layer, idx) => {
    const ids = [docLayerId(layer, idx)];
    if (layer.layerType === 'fill' && layer.outline) {
      ids.push(outlineLayerId(layer, idx));
    }
    return ids;
  });

/**
 * The Petrotech layers, bottom to top: the footprints (fill, then outline), then the
 * shown map's cells, which draw above the other Petrotech layers (INS-13193).
 */
export const PETROTECH_LAYER_IDS: string[] = [
  ...tileLayerIds(subsurfaceMapsConfig.tileLayers),
  CELLS_LAYER_ID,
];

/**
 * The Areas layers (INS-13107). Areas are context under the point and line datasets,
 * as the mock draws them, but above the Petrotech rasters they would otherwise hide.
 * The selected area's halo stays with them, so it never draws over the wells.
 */
export const AREAS_LAYER_IDS: string[] = [
  ...tileLayerIds(areasConfig.tileLayers),
  ...(areasConfig.tileLayers ?? []).flatMap((layer, idx) =>
    layer.layerType === 'fill' && layer.outline ? [haloLayerId(layer, idx)] : []
  ),
];

/**
 * Everything kept on the basemap under the rest of the app's layers, bottom first.
 * A dataset that is off simply has none of its layers on the map.
 */
export const BOTTOM_LAYER_IDS: string[] = [
  ...PETROTECH_LAYER_IDS,
  ...AREAS_LAYER_IDS,
];

/**
 * Whether a layer belongs to the basemap: the style's background, or a layer drawn
 * from one of the basemap's own Mapbox sources (`mapbox://…`). Every other layer is
 * one the app added — counties and the land grid, the datasets, Portfolio Assets,
 * drawing tools — and all of those draw above the Petrotech layers.
 * @param {StyleLayer} layer The layer.
 * @param {Record<string, StyleSource>} sources The style's sources.
 * @return {boolean} True for a basemap layer.
 */
export function isBasemapLayer(
  layer: StyleLayer,
  sources: Record<string, StyleSource>
): boolean {
  if (typeof layer.source !== 'string') return true;
  const source = sources[layer.source];
  const url = source && 'url' in source ? source.url : undefined;
  return typeof url === 'string' && url.startsWith('mapbox://');
}

/** One move: put `id` directly below `beforeId`, or on top when there is none. */
export interface LayerMove {
  id: string;
  beforeId?: string;
}

/**
 * The moves that put the bottom layers on the basemap and under everything else:
 * directly below the lowest layer the app added — the Petrotech footprints, then
 * their cells, then the Areas fill and outline. Nothing else moves. None when they
 * are already there.
 * @param {StyleLayer[]} layers The style's layers, bottom first.
 * @param {Record<string, StyleSource>} sources The style's sources.
 * @return {LayerMove[]} The moves, in order.
 */
export function petrotechLayerMoves(
  layers: StyleLayer[],
  sources: Record<string, StyleSource>
): LayerMove[] {
  const ids = layers.map((layer) => layer.id);
  const own = BOTTOM_LAYER_IDS.filter((id) => ids.includes(id));
  if (own.length === 0) return [];
  const anchor = layers.find(
    (layer) =>
      !BOTTOM_LAYER_IDS.includes(layer.id) && !isBasemapLayer(layer, sources)
  )?.id;
  const end = anchor ? ids.indexOf(anchor) : ids.length;
  const inPlace = own.every((id, i) => ids[end - own.length + i] === id);
  if (inPlace) return [];
  return own.map((id) => (anchor ? { id, beforeId: anchor } : { id }));
}

/**
 * Applies the Petrotech layer order to the map now, and again after every style
 * change: toggling a layer re-adds it on top, a basemap switch re-adds them all, and
 * showing another map adds its cells. A Petrotech layer that has just been added is
 * placed on the styledata that announces it, before the next frame paints, so it
 * never draws on top in between. Any other change is debounced, since one change
 * fires a burst of styledata events and getStyle() clones the whole style.
 * @param {MapboxMap | undefined} map The Mapbox map.
 */
export function usePetrotechLayerOrder(map: MapboxMap | undefined) {
  useEffect(() => {
    if (!map) return undefined;
    /** Moves the Petrotech layers into place, unless they are there already. */
    const apply = () => {
      let style;
      try {
        style = map.getStyle();
      } catch {
        // The style itself is (re)loading; its next styledata retries.
        return;
      }
      if (!style?.layers) return;
      const moves = petrotechLayerMoves(style.layers, style.sources ?? {});
      moves.forEach(({ id, beforeId }) => {
        if (map.getLayer(id)) map.moveLayer(id, beforeId);
      });
    };
    /**
     * The Petrotech layers on the map now; three lookups, no style clone.
     * @return {string[]} Their ids.
     */
    const present = () => {
      try {
        return BOTTOM_LAYER_IDS.filter((id) => map.getLayer(id));
      } catch {
        return [];
      }
    };
    const applySoon = debounce(apply, 50);
    let onMap = present();
    /** Places a just-added Petrotech layer now; anything else waits out the burst. */
    const onStyleData = () => {
      const now = present();
      const added = now.some((id) => !onMap.includes(id));
      onMap = now;
      if (added) {
        applySoon.cancel();
        apply();
      } else {
        applySoon();
      }
    };
    apply();
    map.on('styledata', onStyleData);
    return () => {
      map.off('styledata', onStyleData);
      applySoon.cancel();
    };
  }, [map]);
}
